import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  File, FileText, Image, Video, Award, GraduationCap, BookOpen,
  Download, FolderOpen, ChevronDown, ChevronRight, Globe, Loader2,
  Users, Bookmark, Heart, Star, Code, FileSpreadsheet, Presentation,
  BarChart, FileImage, Film, Trophy, Newspaper, ArrowLeft, Eye,
  Mail, MapPin, Linkedin, User
} from 'lucide-react';
import ProfilePictureFrame from '../components/ProfilePictureFrame';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');

const ALL_CATEGORIES = [
  { id: 'word', label: 'Word Documents', icon: FileText },
  { id: 'excel', label: 'Excel Spreadsheets', icon: FileSpreadsheet },
  { id: 'powerpoint', label: 'PowerPoint Presentations', icon: Presentation },
  { id: 'images', label: 'Pictures/Images', icon: FileImage },
  { id: 'videos', label: 'Videos', icon: Film },
  { id: 'awards', label: 'Awards', icon: Trophy },
  { id: 'certificates', label: 'Certificates', icon: GraduationCap },
  { id: 'projects', label: 'Projects', icon: Code },
  { id: 'leadership', label: 'Leadership & Team Development', icon: Users },
  { id: 'case_studies', label: 'Case Studies', icon: Bookmark },
  { id: 'community_service', label: 'Community & Service', icon: Heart },
  { id: 'performance_evaluations', label: 'Performance Evaluations', icon: Star },
  { id: 'publications', label: 'Publications', icon: Newspaper },
  { id: 'training', label: 'Training Materials', icon: BookOpen },
  { id: 'reports', label: 'Reports & Analysis', icon: BarChart },
  { id: 'other', label: 'Other', icon: File },
];

const PortfolioPublicViewPage = () => {
  const { publicLinkId } = useParams();
  const [files, setFiles] = useState([]);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState({});

  useEffect(() => {
    fetchPublicPortfolio();
  }, [publicLinkId]);

  const fetchPublicPortfolio = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/public/portfolio/${encodeURIComponent(publicLinkId)}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('This portfolio was not found or has no public files.');
        } else {
          setError('Failed to load portfolio.');
        }
        return;
      }
      const data = await res.json();
      setFiles(data.files || []);
      setProfile(data);

      const hasFiles = {};
      (data.files || []).forEach(f => { hasFiles[f.category || 'other'] = true; });
      setExpandedFolders(hasFiles);
    } catch (err) {
      console.error('[PublicPortfolio]', err);
      setError('Failed to load portfolio. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (categoryId) => {
    setExpandedFolders(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filesByCategory = files.reduce((acc, file) => {
    const cat = file.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(file);
    return acc;
  }, {});

  const categoriesWithFiles = ALL_CATEGORIES.filter(cat => (filesByCategory[cat.id] || []).length > 0);

  const formatNameWithCredentials = () => {
    const name = profile.ownerName || 'Portfolio';
    const creds = profile.credentials;
    if (creds) return `${name}, ${creds}`;
    return name;
  };

  const universityInfo = (() => {
    const edu = profile.education;
    if (!Array.isArray(edu) || edu.length === 0) return null;
    const current = edu.find(e => !e.end_date || e.end_date === 'Present' || e.is_expected);
    if (current) return current;
    return edu[0];
  })();

  const hasContactInfo = profile.email || profile.location || profile.linkedinUrl || profile.websiteUrl;
  const highlightColor = profile.highlightColor || '#3b82f6';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <FolderOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Portfolio Not Available</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Profile-style Banner ── */}
      <div
        className="h-64 bg-gradient-to-r from-primary-600 to-primary-800 relative w-full"
        style={
          profile.bannerUrl
            ? {
                backgroundImage: `linear-gradient(rgba(37, 99, 235, 0.3), rgba(29, 78, 216, 0.3)), url(${profile.bannerUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-end pb-8 relative">
          {/* Profile Picture */}
          <div className="absolute -bottom-12 left-8 z-20">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full shadow-2xl"
                style={{ backgroundColor: highlightColor, padding: '6px' }}
              >
                <div className="w-full h-full rounded-full bg-white"></div>
              </div>
              <ProfilePictureFrame
                photoUrl={profile.photoUrl}
                frameOption={profile.profileFrame}
                size="w-48 h-48"
                borderColor={highlightColor}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Profile Information Section ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start">
          {/* Left: Name, headline, education */}
          <div className="min-w-0">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">{formatNameWithCredentials()}</h1>

            {profile.pronouns && (
              <p className="text-sm text-gray-600 mb-2">{profile.pronouns}</p>
            )}

            {universityInfo && (
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                {universityInfo.institution_logo ? (
                  <img
                    src={universityInfo.institution_logo}
                    alt={`${universityInfo.institution} logo`}
                    className="w-8 h-8 object-contain rounded"
                  />
                ) : (
                  <GraduationCap className="w-5 h-5 flex-shrink-0" />
                )}
                <span className="text-base">
                  {universityInfo.institution}
                  {universityInfo.degree && ` \u2022 ${universityInfo.degree}`}
                  {universityInfo.end_date && universityInfo.end_date !== 'Present' && ` \u2022 ${universityInfo.end_date}`}
                  {(!universityInfo.end_date || universityInfo.end_date === 'Present') && ' \u2022 Currently Attending'}
                </span>
              </div>
            )}

            {profile.headline && (
              <p className="text-lg text-gray-700 leading-relaxed max-w-xl mt-4" style={{ lineHeight: '1.6' }}>
                {profile.headline}
              </p>
            )}
          </div>

          {/* Right: Contact Information */}
          {hasContactInfo && (
            <div className="bg-white rounded-lg shadow-md p-6 w-full lg:w-auto lg:min-w-[280px]">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                {profile.email && (
                  <a
                    href={`mailto:${profile.email}`}
                    className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    <span className="text-sm">{profile.email}</span>
                  </a>
                )}
                {profile.location && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <MapPin className="w-5 h-5" />
                    <span className="text-sm">{profile.location}</span>
                  </div>
                )}
                {profile.linkedinUrl && (
                  <a
                    href={profile.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                    <span className="text-sm">LinkedIn Profile</span>
                  </a>
                )}
                {profile.websiteUrl && (
                  <a
                    href={profile.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <Globe className="w-5 h-5" />
                    <span className="text-sm">Personal Website</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Portfolio Files Content ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Eye className="w-6 h-6 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900">
            Public Portfolio
          </h2>
          <span className="text-sm text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">
            {files.length} file{files.length !== 1 ? 's' : ''}
          </span>
        </div>

        {categoriesWithFiles.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Public Files</h2>
            <p className="text-gray-500">This portfolio does not have any publicly shared files.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {categoriesWithFiles.map(cat => {
              const catFiles = filesByCategory[cat.id] || [];
              const isExpanded = !!expandedFolders[cat.id];
              const IconComponent = cat.icon;

              return (
                <div key={cat.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <button
                    onClick={() => toggleFolder(cat.id)}
                    className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="bg-primary-100 p-2 rounded-lg flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-gray-900">{cat.label}</span>
                    </div>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full flex-shrink-0">
                      {catFiles.length} file{catFiles.length !== 1 ? 's' : ''}
                    </span>
                  </button>

                  {isExpanded && (
                    <div className="border-t border-gray-100 px-5 py-4">
                      <div className="space-y-3">
                        {catFiles.map(fileObj => (
                          <div key={fileObj.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-all">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="bg-gray-100 p-2 rounded-lg mt-0.5 flex-shrink-0">
                                  <IconComponent className="w-4 h-4 text-gray-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 truncate">{fileObj.name}</p>
                                  <p className="text-sm text-gray-500">
                                    {formatFileSize(fileObj.size)}
                                    {fileObj.uploadedAt && ` \u2022 ${new Date(fileObj.uploadedAt).toLocaleDateString()}`}
                                  </p>
                                  {fileObj.summary && (
                                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">{fileObj.summary}</p>
                                  )}
                                </div>
                              </div>
                              {fileObj.url && (
                                <a
                                  href={fileObj.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors ml-3 flex-shrink-0"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  Open
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Google Sites link if the owner has one */}
        {profile.googleSiteUrl && (
          <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center">
            <Globe className="w-8 h-8 text-primary-600 mx-auto mb-3" />
            <p className="text-gray-700 mb-3 font-medium">View additional portfolio content</p>
            <a
              href={profile.googleSiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Visit Google Sites Portfolio
            </a>
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-sm text-gray-400">
            Powered by CareerBrandNetwork
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortfolioPublicViewPage;
